"use client"

import { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"; 
import { Loader2, AlertCircle } from "lucide-react"

import { FormField } from "../components/auth/FormField" 
import { SetPasswordHeader } from "../components/auth/SetPasswordHeader" 
import { PasswordStrengthIndicator } from "../components/auth/PasswordStrengthIndicator" 
import { SuccessModal } from "../components/auth/SuccessModal" 

// Validation schema
const schema = z.object({
  password: z
    .string().min(1, "Password is required"),

  confirmPassword: z
    .string()
    .min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords must match",
  path: ["confirmPassword"],
})

const SetPasswordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState("")
  const [tokenValid, setTokenValid] = useState(true)

  const token = searchParams.get("token")

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  })

  const password = watch("password")

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false)
      setError("Invalid or missing token. Please check your link.")
      return
    }

    // Here you would validate the token with your API
    // For now, we'll assume it's valid
    validateToken(token)
  }, [token])

  const validateToken = async (token: string) => {
    try {
      // Replace with your actual API call
      // const response = await api.validateSetPasswordToken(token)
      // if (!response.valid) {
      //   setTokenValid(false)
      //   setError("Token has expired or is invalid")
      // }
      console.log("Validating token:", token)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      setTokenValid(false)
      setError("Failed to validate token. Please try again.")
    }
  }

  const onSubmit = async () => {
    if (!tokenValid) return

    setIsLoading(true)
    setError("")

    try {
      // Replace with your actual API call
      // await api.setPassword({
      //   token,
      //   password: data.password
      // })

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000))

      setShowSuccess(true)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      setError("Failed to set password. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessContinue = () => {
    navigate("/auth?mode=signin")
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-2xl flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Token</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/auth?mode=signin")}
            className="w-full bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Go to Sign In
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 flex items-center justify-center p-4">
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-2xl max-w-sm w-full mx-4"
            >
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Setting Your Password...</h3>
                  <p className="text-gray-600 text-sm">Please wait while we secure your account</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SuccessModal isOpen={showSuccess} onContinue={handleSuccessContinue} />

      {/* Main Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 lg:p-8 my-8"
      >
        <SetPasswordHeader />

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-red-700 text-sm font-medium">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Password Field - Using reusable FormField */}
          <FormField
            name="password"
            label="New Password"
            placeholder="Create a strong password"
            control={control}
            errors={errors}
            type="password"
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            delay={0.1}
          />

          {/* Password Strength Indicator */}
          {password && <PasswordStrengthIndicator password={password} />}

          {/* Confirm Password Field - Using reusable FormField */}
          <FormField
            name="confirmPassword"
            label="Confirm Password"
            placeholder="Confirm your password"
            control={control}
            errors={errors}
            type="password"
            showPassword={showConfirmPassword}
            onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
            delay={0.2}
          />

          {/* Submit Button */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-14 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-semibold text-lg rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <div className="flex items-center justify-center space-x-2">
                {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                <span>{isLoading ? "Setting Password..." : "Set Password"}</span>
              </div>
            </button>
          </motion.div>

          {/* Security Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center pt-4 border-t border-gray-100"
          >
            <p className="text-xs text-gray-500">🔒 Your password will be encrypted and stored securely</p>
          </motion.div>
        </form>
      </motion.div>
    </div>
  )
}

export default SetPasswordPage
